import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiPhone, FiPhoneOff, FiVideo, FiVideoOff, FiMic, FiMicOff, FiMaximize2, FiMinimize2, FiMonitor } from 'react-icons/fi';
import {
  createCallWithOffer, updateCallStatus, setAnswer,
  addIceCandidate, subscribeToIceCandidates, subscribeToCall,
  subscribeToIncomingCalls, cleanupCall, ICE_SERVERS
} from '../../services/callService';
import { useAuth } from '../../hooks/useAuth';

const RING_DURATION = 30000; // 30s ring timeout

const VideoCall = () => {
  const { user } = useAuth();
  const [callState, setCallState] = useState('idle'); // idle | calling | ringing | connected | ended
  const [currentCall, setCurrentCall] = useState(null);
  const [isVideo, setIsVideo] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isCamOff, setIsCamOff] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [incomingCall, setIncomingCall] = useState(null);
  const callStateRef = useRef('idle');

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnection = useRef(null);
  const localStream = useRef(null);
  const unsubCall = useRef(null);
  const unsubCandidates = useRef(null);
  const ringTimeout = useRef(null);
  const durationInterval = useRef(null);
  const containerRef = useRef(null);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (localStream.current) {
      localStream.current.getTracks().forEach(t => t.stop());
      localStream.current = null;
    }
    if (peerConnection.current) {
      peerConnection.current.close();
      peerConnection.current = null;
    }
    if (unsubCall.current) { unsubCall.current(); unsubCall.current = null; }
    if (unsubCandidates.current) { unsubCandidates.current(); unsubCandidates.current = null; }
    if (ringTimeout.current) { clearTimeout(ringTimeout.current); ringTimeout.current = null; }
    if (durationInterval.current) { clearInterval(durationInterval.current); durationInterval.current = null; }
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    setCallDuration(0);
  }, []);

  // Keep ref in sync
  useEffect(() => { callStateRef.current = callState; }, [callState]);

  // Listen for incoming calls
  useEffect(() => {
    if (!user?.uid) return;
    console.log('[VideoCall] Subscribing to incoming calls for', user.uid);
    const unsub = subscribeToIncomingCalls(user.uid, (call) => {
      console.log('[VideoCall] Incoming call received:', call.id, 'state:', callStateRef.current);
      if (callStateRef.current === 'idle') {
        setIncomingCall(call);
        setCallState('ringing');
        playRingSound();
        ringTimeout.current = setTimeout(() => {
          if (callStateRef.current === 'ringing') {
            rejectCall();
          }
        }, RING_DURATION);
      }
    });
    return () => unsub();
  }, [user?.uid]); // Only depend on user.uid, not callState

  const playRingSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const playTone = (freq, start, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        gain.gain.value = 0.15;
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + dur);
      };
      // Ring pattern: two tones, pause, repeat
      for (let i = 0; i < 3; i++) {
        playTone(440, i * 1.5, 0.4);
        playTone(520, i * 1.5 + 0.5, 0.4);
      }
    } catch (e) { /* silent */ }
  };

  const getMediaStream = async (video) => {
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: video ? { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' } : false
      });
    } catch (e) {
      // Fallback to audio only
      if (video) {
        return await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      }
      throw e;
    }
  };

  const createPeerConnection = () => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    pc.ontrack = (event) => {
      if (remoteVideoRef.current && event.streams[0]) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
    };

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
        endCall();
      }
    };

    return pc;
  };

  // Start a call (caller side)
  const startCall = async (calleeUser, videoCall = true) => {
    if (!user?.uid) return;
    setIsVideo(videoCall);
    setCallState('calling');

    try {
      const stream = await getMediaStream(videoCall);
      localStream.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      const pc = createPeerConnection();
      peerConnection.current = pc;

      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      // Create offer FIRST
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Create call document WITH the offer included (so callee gets it immediately)
      const callId = await createCallWithOffer(user.uid, user, calleeUser.uid, calleeUser, offer, videoCall);

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          addIceCandidate(callId, event.candidate, 'caller');
        }
      };

      setCurrentCall({ id: callId, calleeInfo: calleeUser, isVideo: videoCall });

      // Listen for answer
      unsubCall.current = subscribeToCall(callId, async (callData) => {
        if (callData.status === 'answered' && callData.answer && !pc.currentRemoteDescription) {
          await pc.setRemoteDescription(new RTCSessionDescription(callData.answer));
          setCallState('connected');
          startDurationTimer();

          // Listen for callee ICE candidates
          unsubCandidates.current = subscribeToIceCandidates(callId, 'caller', async (candidateData) => {
            try {
              await pc.addIceCandidate(new RTCIceCandidate(candidateData));
            } catch (e) { /* ignore */ }
          });
        } else if (callData.status === 'ended' || callData.status === 'missed') {
          endCall();
        }
      });

      // Ring timeout
      ringTimeout.current = setTimeout(() => {
        if (callState === 'calling') {
          updateCallStatus(callId, 'missed');
          endCall();
        }
      }, RING_DURATION);

    } catch (e) {
      console.error('Error starting call:', e);
      setCallState('idle');
      cleanup();
    }
  };

  // Answer an incoming call
  const answerCall = async () => {
    if (!incomingCall) return;
    clearTimeout(ringTimeout.current);

    try {
      const stream = await getMediaStream(incomingCall.isVideo);
      localStream.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      setIsVideo(incomingCall.isVideo);

      const pc = createPeerConnection();
      peerConnection.current = pc;

      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          addIceCandidate(incomingCall.id, event.candidate, 'callee');
        }
      };

      // Set remote offer
      await pc.setRemoteDescription(new RTCSessionDescription(incomingCall.offer));

      // Create answer
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await setAnswer(incomingCall.id, answer);
      await updateCallStatus(incomingCall.id, 'answered');

      setCurrentCall(incomingCall);
      setCallState('connected');
      setIncomingCall(null);
      startDurationTimer();

      // Listen for caller ICE candidates
      unsubCandidates.current = subscribeToIceCandidates(incomingCall.id, 'callee', async (candidateData) => {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidateData));
        } catch (e) { /* ignore */ }
      });

      // Listen for call status changes
      unsubCall.current = subscribeToCall(incomingCall.id, (callData) => {
        if (callData.status === 'ended') {
          endCall();
        }
      });

    } catch (e) {
      console.error('Error answering call:', e);
      rejectCall();
    }
  };

  const rejectCall = async () => {
    if (incomingCall) {
      await updateCallStatus(incomingCall.id, 'missed');
      setIncomingCall(null);
    }
    setCallState('idle');
    cleanup();
  };

  const endCall = async () => {
    if (currentCall?.id) {
      try { await updateCallStatus(currentCall.id, 'ended'); } catch (e) { /* */ }
      setTimeout(() => cleanupCall(currentCall.id), 5000);
    }
    setCurrentCall(null);
    setIncomingCall(null);
    setCallState('idle');
    cleanup();
  };

  const startDurationTimer = () => {
    const start = Date.now();
    durationInterval.current = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - start) / 1000));
    }, 1000);
  };

  const toggleMute = () => {
    if (localStream.current) {
      localStream.current.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
      setIsMuted(!isMuted);
    }
  };

  const toggleCamera = () => {
    if (localStream.current) {
      localStream.current.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
      setIsCamOff(!isCamOff);
    }
  };

  // Find the video sender reliably (via transceivers or sender track)
  const getVideoSender = () => {
    const pc = peerConnection.current;
    if (!pc) return null;
    // Try transceivers first (most reliable)
    if (pc.getTransceivers) {
      const tc = pc.getTransceivers().find(t => t.sender?.track?.kind === 'video' || (t.mid !== null && t.receiver?.track?.kind === 'video'));
      if (tc) return tc.sender;
    }
    // Fallback: search senders
    return pc.getSenders().find(s => s.track?.kind === 'video') || null;
  };

  const toggleScreenShare = async () => {
    const pc = peerConnection.current;
    if (!pc) return;
    try {
      if (isScreenSharing) {
        // Switch back to camera
        const camStream = await getMediaStream(isVideo);
        const videoTrack = camStream.getVideoTracks()[0];
        const sender = getVideoSender();
        if (sender) {
          await sender.replaceTrack(videoTrack);
        } else {
          // No video sender exists, add the track (triggers renegotiation)
          pc.addTrack(videoTrack, localStream.current);
        }
        // Update local stream
        localStream.current.getVideoTracks().forEach(t => { t.stop(); localStream.current.removeTrack(t); });
        localStream.current.addTrack(videoTrack);
        if (localVideoRef.current) localVideoRef.current.srcObject = new MediaStream(localStream.current.getTracks());
        setIsScreenSharing(false);
      } else {
        // Share screen
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: { cursor: 'always', width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: true
        });
        const screenTrack = screenStream.getVideoTracks()[0];
        const sender = getVideoSender();
        if (sender) {
          await sender.replaceTrack(screenTrack);
        } else {
          pc.addTrack(screenTrack, localStream.current);
        }
        // Also share system audio if available
        const audioTracks = screenStream.getAudioTracks();
        if (audioTracks.length > 0) {
          pc.addTrack(audioTracks[0], screenStream);
        }
        // Update local preview to show screen
        localStream.current.getVideoTracks().forEach(t => { t.stop(); localStream.current.removeTrack(t); });
        localStream.current.addTrack(screenTrack);
        if (localVideoRef.current) localVideoRef.current.srcObject = new MediaStream(localStream.current.getTracks());
        // When user stops sharing via browser UI
        screenTrack.onended = () => {
          toggleScreenShare();
        };
        setIsScreenSharing(true);
      }
    } catch (e) {
      console.error('Screen share error:', e);
      setIsScreenSharing(false);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen && containerRef.current) {
      containerRef.current.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  const formatDuration = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  const remoteName = currentCall?.calleeInfo?.name || currentCall?.callerInfo?.name || 'Usuario';
  const remotePhoto = currentCall?.calleeInfo?.photo || currentCall?.callerInfo?.photo;

  // Expose startCall to parent via window event
  useEffect(() => {
    const handleStartCall = (e) => {
      const { calleeUser, isVideo: vid } = e.detail;
      startCall(calleeUser, vid);
    };
    window.addEventListener('start-call', handleStartCall);
    return () => window.removeEventListener('start-call', handleStartCall);
  }, [user]);

  if (callState === 'idle') return null;

  // Incoming call ring screen
  if (callState === 'ringing' && incomingCall) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center backdrop-blur-sm">
        <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-8 w-80 text-center shadow-2xl animate-in">
          <div className="mb-6">
            {incomingCall.callerInfo?.photo ? (
              <img src={incomingCall.callerInfo.photo} alt="" className="w-24 h-24 rounded-full mx-auto ring-4 ring-green-400 animate-pulse" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-green-500 mx-auto flex items-center justify-center text-white text-3xl font-bold ring-4 ring-green-400 animate-pulse">
                {(incomingCall.callerInfo?.name || '?')[0].toUpperCase()}
              </div>
            )}
          </div>
          <p className="text-white text-lg font-semibold mb-1">{incomingCall.callerInfo?.name || 'Usuario'}</p>
          <p className="text-slate-400 text-sm mb-8">
            {incomingCall.isVideo ? '📹 Videollamada entrante...' : '📞 Llamada entrante...'}
          </p>
          <div className="flex justify-center gap-8">
            <button onClick={rejectCall}
              className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white shadow-lg shadow-red-500/30 transition-all hover:scale-110">
              <FiPhoneOff size={24} />
            </button>
            <button onClick={answerCall}
              className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center text-white shadow-lg shadow-green-500/30 transition-all hover:scale-110 animate-bounce">
              <FiPhone size={24} />
            </button>
          </div>
        </div>
        <style>{`
          .animate-in { animation: scale-in 0.3s ease-out; }
          @keyframes scale-in { from { opacity:0; transform:scale(0.8); } to { opacity:1; transform:scale(1); } }
        `}</style>
      </div>
    );
  }

  // Active call / calling screen
  return (
    <div ref={containerRef}
      className={`fixed z-[9999] ${isFullscreen ? 'inset-0' : 'bottom-4 right-4 w-[420px] h-[520px] rounded-2xl overflow-hidden shadow-2xl'} bg-slate-900 flex flex-col`}>

      {/* Remote video (full background) */}
      <div className="flex-1 relative bg-black">
        <video ref={remoteVideoRef} autoPlay playsInline
          className="w-full h-full object-cover" />

        {/* No video placeholder */}
        {(callState !== 'connected' || !isVideo) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-slate-800 to-slate-900">
            {remotePhoto ? (
              <img src={remotePhoto} alt="" className="w-28 h-28 rounded-full mb-4 ring-4 ring-white/20" />
            ) : (
              <div className="w-28 h-28 rounded-full bg-slate-600 mb-4 flex items-center justify-center text-white text-4xl font-bold ring-4 ring-white/20">
                {remoteName[0]?.toUpperCase() || '?'}
              </div>
            )}
            <p className="text-white text-xl font-semibold">{remoteName}</p>
            <p className="text-slate-400 text-sm mt-2">
              {callState === 'calling' && 'Llamando...'}
              {callState === 'connected' && !isVideo && formatDuration(callDuration)}
            </p>
            {callState === 'calling' && (
              <div className="flex gap-1 mt-4">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            )}
          </div>
        )}

        {/* Local video (picture-in-picture) */}
        {isVideo && (
          <div className="absolute top-3 right-3 w-32 h-24 rounded-xl overflow-hidden bg-slate-800 shadow-lg ring-2 ring-white/10">
            <video ref={localVideoRef} autoPlay playsInline muted
              className="w-full h-full object-cover mirror" />
          </div>
        )}

        {/* Duration badge */}
        {callState === 'connected' && isVideo && (
          <div className="absolute top-3 left-3 flex items-center gap-2">
            <span className="px-3 py-1 rounded-full bg-black/50 text-white text-xs font-mono backdrop-blur-sm">
              {formatDuration(callDuration)}
            </span>
            {isScreenSharing && (
              <span className="px-3 py-1 rounded-full bg-blue-500/80 text-white text-xs font-medium backdrop-blur-sm flex items-center gap-1">
                <FiMonitor size={12} /> Compartiendo pantalla
              </span>
            )}
          </div>
        )}
      </div>

      {/* Controls bar */}
      <div className="bg-slate-900/95 backdrop-blur px-4 py-4 flex items-center justify-center gap-4">
        <button onClick={toggleMute}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${isMuted ? 'bg-red-500 text-white' : 'bg-slate-700 text-white hover:bg-slate-600'}`}
          title={isMuted ? 'Activar micrófono' : 'Silenciar'}>
          {isMuted ? <FiMicOff size={20} /> : <FiMic size={20} />}
        </button>

        {isVideo && (
          <button onClick={toggleCamera}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${isCamOff ? 'bg-red-500 text-white' : 'bg-slate-700 text-white hover:bg-slate-600'}`}
            title={isCamOff ? 'Activar cámara' : 'Desactivar cámara'}>
            {isCamOff ? <FiVideoOff size={20} /> : <FiVideo size={20} />}
          </button>
        )}

        <button onClick={toggleScreenShare}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${isScreenSharing ? 'bg-blue-500 text-white ring-2 ring-blue-300' : 'bg-slate-700 text-white hover:bg-slate-600'}`}
          title={isScreenSharing ? 'Dejar de compartir' : 'Compartir pantalla'}>
          <FiMonitor size={20} />
        </button>

        <button onClick={endCall}
          className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white shadow-lg shadow-red-500/30 transition-all hover:scale-105"
          title="Colgar">
          <FiPhoneOff size={22} />
        </button>

        <button onClick={toggleFullscreen}
          className="w-12 h-12 rounded-full bg-slate-700 text-white hover:bg-slate-600 flex items-center justify-center transition-all"
          title={isFullscreen ? 'Salir pantalla completa' : 'Pantalla completa'}>
          {isFullscreen ? <FiMinimize2 size={20} /> : <FiMaximize2 size={20} />}
        </button>
      </div>

      <style>{`
        .mirror { transform: scaleX(-1); }
      `}</style>
    </div>
  );
};

export default VideoCall;
